import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { create } from "zustand";
import { useAuth } from "@/data/auth";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input";
import { IconButton } from "@/ui/IconButton";
import { LogoMark } from "@/ui/Logo";
import s from "./SignInDialog.module.css";

interface SignInDialogStore {
  open: boolean;
  setOpen: (open: boolean) => void;
}
export const useSignInDialog = create<SignInDialogStore>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

export function SignInDialog() {
  const { open, setOpen } = useSignInDialog();
  const { providers, requestMagicLink } = useAuth();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "queued">("idle");
  const [error, setError] = useState("");

  const submit = async () => {
    if (!email.trim() || state === "sending") return;
    setState("sending");
    setError("");
    const res = await requestMagicLink(email.trim());
    if (res.ok) setState(res.queued ? "queued" : "sent");
    else {
      setState("idle");
      setError(res.error ?? "something went wrong");
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setState("idle");
          setError("");
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.dialog}>
          <div className={s.header}>
            <LogoMark size={26} />
            <Dialog.Title className={s.title}>Back up your notes</Dialog.Title>
          </div>
          <Dialog.Description className={s.subtitle}>
            Sign in and your notes sync to the cloud, free — live across every device, safe if this browser is lost.
          </Dialog.Description>

          {state === "sent" || state === "queued" ? (
            <div className={s.sent}>
              Check <strong>{email}</strong> for your sign-in link. It expires in 15 minutes.
              {state === "queued" ? " Delivery may take a little longer than usual right now." : ""}
            </div>
          ) : (
            <>
              {providers.magicLink ? (
                <form
                  className={s.form}
                  onSubmit={(e) => {
                    e.preventDefault();
                    void submit();
                  }}
                >
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    autoFocus
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {error ? <span className={s.error}>{error}</span> : null}
                  <Button variant="solid" type="submit" disabled={state === "sending"}>
                    {state === "sending" ? "Sending…" : "Email me a sign-in link"}
                  </Button>
                </form>
              ) : null}
              {providers.magicLink && providers.hepgg ? <div className={s.divider}>or</div> : null}
              {providers.hepgg ? (
                <a className={s.hepgg} href="/api/auth/hepgg">
                  Continue with Hep.gg
                </a>
              ) : null}
              {!providers.magicLink && !providers.hepgg ? (
                <div className={s.sent}>Sign-in isn't configured on this server yet.</div>
              ) : null}
            </>
          )}

          <p className={s.privacy}>No password. No spam. Your notes stay yours.</p>

          <Dialog.Close asChild>
            <IconButton label="Close" className={s.close}>
              <X size={14} />
            </IconButton>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
